import React, { FC, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/AuthContext';
import useAPIError from '../../hooks/useAPIError';
import { BackErrorType } from '../../types/Types';
import MisValesAhorroService from '../../services/MisValesAhorroService';
import { useAppStore } from '../../store/AppContext';
import { AppMappings } from '../../utils/AppMappings';
import { FrontMappings } from '../../utils/FrontMappings';
import routerLoader from '../../i18n/router';
import { MisDatosConstants } from '../../utils/MisDatosConstants';
import { HostProvider } from '../../utils/HostProvider';
import ModalConfirmacion from '../../components/ModalConfirmacion';
import { StorageHelper } from '../../utils/StorageHelper';
import { MisDatosHelper } from './MisDatosHelper';
import DeepLinkService from '../../services/DeepLinkService';
import { DeepLinkMappings } from '../../utils/DeepLinkMappings';

const PreferenciaModVales: FC = () => {
  const dataUser = useAuthStore();
  const { t } = useTranslation();
  const isEroski = HostProvider.isEroski();
  const isCaprabo = HostProvider.isCaprabo();
  const dataApp = useAppStore();
  const isApp = dataUser.user.isApp === 'true';
  const [checkPendingModal, setCheckPendingModal] = useState<boolean>(false);
  const [isClienteDigital, setIsClienteDigital] = useState<boolean>(false);
  const [isClienteCaja, setIsClienteCaja] = useState<boolean>(false);
  const [isEroskiDigital, setIsEroskiDigital] = useState<boolean>(false);
  const [isEroskiCaja, setIsEroskiCaja] = useState<boolean>(false);
  const [isRedirectVales, setIsRedirectVales] = useState<boolean>(false);
  const [showModalPreferencia, setShowModalPreferencia] = useState(
    false as boolean,
  );
  const [
    aceptaCambioPreferencia,
    setAceptaCambioPreferencia,
  ] = useState<string>('-1');
  const [keyPendingModal, setKeyPendingModal] = useState('' as string);
  const [modalTitleTxt, setModalTitleTxt] = useState('' as string);
  const [modalBodyTxt, setModalBodyTxt] = useState('' as string);
  const [modalBtnAceptTxt, setModalBtnAceptTxt] = useState('' as string);
  const [modalBtnCancelTxt, setModalBtnCancelTxt] = useState('' as string);
  const [modalPieTxt, setModalPieTxt] = useState('' as string);
  const [load, setLoad] = useState<boolean>(false);
  const [save, setSave] = useState<boolean>(false);
  const { addErrorBack } = useAPIError();
  const [preference, setPreference] = useState<string>('');
  const [preferenceOld, setPreferenceOld] = useState<string>('');
  const tlfMsgErr = isEroski ? t('footer.phone') : t('caprabo.phone');
  const errorResponse: BackErrorType = {
    errorCode: -1,
    title: [{ key: 'nlr-vaya-algo-no-ha-ido-bien', arguments: [] }],
    userMessage: [{ key: 'error.exception', arguments: [tlfMsgErr] }],
  };
  const tituloPreferencia = isEroski
    ? 'datos.conf-vales-ahorro.title'
    : 'caprabo.cupones.preferencia.conf-vales-ahorro-titulo';
  const subTituloPreferencia = isEroski
    ? 'datos.conf-vales-ahorro.subtitle'
    : 'caprabo.cupones.preferencia.conf-vales-ahorro-subtitulo';

  function setPreferencia(preferencia) {
    setIsClienteDigital(
      preferencia === MisDatosConstants.CLIENTE_PROMOCION_DIGITAL,
    );
    setIsClienteCaja(preferencia === MisDatosConstants.CLIENTE_PROMOCION_CAJA);
    setIsEroskiDigital(
      preferencia === MisDatosConstants.EROSKI_PROMOCION_DIGITAL,
    );
    setIsEroskiCaja(preferencia === MisDatosConstants.EROSKI_PROMOCION_CAJA);
    setPreference(preferencia);
    setPreferenceOld(preferencia);
  }

  const getData = () => {
    MisValesAhorroService.getValesPreferencia()
      .then((response: AxiosResponse) => {
        const { error } = response.data;
        if (error) {
          setPreferencia(' ');
        } else {
          const { preferencia } = response.data.pojo;
          setPreferencia(preferencia);
        }
      })
      .catch((e) => {
        console.log(e);
        setPreferencia(' ');
      });
    setLoad(true);
  };

  const getWebView = () => {
    return routerLoader.LoadRouteByPathChoosingBetweenAppOrWeb(
      FrontMappings.VALES,
      AppMappings.VALES,
      dataApp.lang,
      isCaprabo,
      isApp,
    );
  };

  const getDeepLink = async () => {
    let pathDeepLink: string[] = new Array<string>();
    await DeepLinkService.getUrlByDeepLink({
      locale: dataApp.lang,
      isWeb: !isApp,
      destino: [DeepLinkMappings.linkCupones],
    })
      .then((response) => {
        const { data } = response;
        if (
          data === '' ||
          data.pojo === null ||
          data.pojo.url === null ||
          data.error ||
          data.pojo.error
        ) {
          pathDeepLink[0] = getWebView();
        } else {
          pathDeepLink = data.pojo.url;
        }
      })
      .catch((e) => {
        console.log(e);
        pathDeepLink[0] = getWebView();
      });
    return pathDeepLink[0];
  };

  const modificarPreferencia = () => {
    MisValesAhorroService.updateValesPreferencia({
      anteriorPreferencia: preferenceOld,
      preferencia: preference,
      origen: MisDatosConstants.ORIGEN_MODIFICACION_PREFERENCIA,
    })
      .then(async (response) => {
        const { data } = response;
        if (data === '' || data.pojo === null || data.error) {
          setPreferencia(preferenceOld);
          setIsRedirectVales(false);
          addErrorBack(errorResponse);
        } else {
          setPreferencia(preference);
          if (isRedirectVales) {
            const path = await getDeepLink();
            window.location.href = path;
          }
        }
      })
      .catch((e) => {
        console.log(e);
        addErrorBack(errorResponse);
      });
  };

  function marcarCheckDesmarcado(checkDigital: boolean) {
    if (checkDigital) {
      setIsClienteDigital(true);
    } else {
      setIsClienteCaja(true);
    }
  }

  function revertirCambioCheck(checkDigital: boolean) {
    if (!isClienteCaja && !isClienteDigital) {
      setIsClienteDigital(false);
      setIsClienteCaja(false);
    } else {
      setIsClienteDigital(!checkDigital);
      setIsClienteCaja(checkDigital);
    }
  }

  function marcarPreferencia(valorCheck: boolean, checkDigital: boolean) {
    if (valorCheck) {
      revertirCambioCheck(checkDigital);
    } else if (!valorCheck) {
      marcarCheckDesmarcado(checkDigital);
    }
  }

  function actualizarPreferencia(
    keyPreferenciaCliente: string,
    valorCheck: boolean,
  ) {
    if (keyPreferenciaCliente === MisDatosConstants.CLIENTE_PROMOCION_DIGITAL) {
      marcarPreferencia(valorCheck, true);
    } else if (
      keyPreferenciaCliente === MisDatosConstants.CLIENTE_PROMOCION_CAJA
    ) {
      marcarPreferencia(valorCheck, false);
    }
  }

  const pulsaBtnCancelar = () => {
    setAceptaCambioPreferencia('0');
    setIsRedirectVales(false);
    setPreference(preferenceOld);
    actualizarPreferencia(keyPendingModal, checkPendingModal);
    setShowModalPreferencia(false);
  };

  const pulsaBtnAceptar = () => {
    setAceptaCambioPreferencia('1');
    setIsRedirectVales(true);
    setSave(true);
    setShowModalPreferencia(false);
  };

  function tiposAvisoPreferenciaModal(keyPreferenciaCliente: string) {
    let isECtoCD: boolean;
    let isCCtoCD = false;
    let isEDtoCC = false;
    let isCDtoCC = false;
    const consultaPreferenciaKO: boolean =
      !isEroskiCaja && !isEroskiDigital && !isClienteCaja && !isClienteDigital;
    if (
      consultaPreferenciaKO &&
      MisDatosConstants.CLIENTE_PROMOCION_DIGITAL === keyPreferenciaCliente
    ) {
      isECtoCD = true;
    } else {
      isECtoCD =
        isEroskiCaja &&
        MisDatosConstants.CLIENTE_PROMOCION_DIGITAL === keyPreferenciaCliente;
      isCCtoCD =
        isClienteCaja &&
        MisDatosConstants.CLIENTE_PROMOCION_DIGITAL === keyPreferenciaCliente;
      isEDtoCC =
        isEroskiDigital &&
        MisDatosConstants.CLIENTE_PROMOCION_CAJA === keyPreferenciaCliente;
      isCDtoCC =
        isClienteDigital &&
        MisDatosConstants.CLIENTE_PROMOCION_CAJA === keyPreferenciaCliente;
    }
    return { isECtoCD, isCCtoCD, isEDtoCC, isCDtoCC };
  }

  function buildTextPreferencia(
    isECtoCD: boolean,
    isCCtoCD: boolean,
    isEDtoCC: boolean,
    isCDtoCC: boolean,
  ) {
    let titulo = '';
    let cuerpo = '';
    let botonAceptar = '';
    let pie = '';
    if (isECtoCD) {
      titulo = 'vales.preferencia.titulo-desde-eroski';
      if (isEroski) {
        cuerpo = 'vales.preferencia.cuerpo-ec-to-cd';
        botonAceptar = 'vales.preferencia.boton-activarVales';
        pie = 'vales.preferencia.pie-ec-cd';
      } else if (isCaprabo) {
        cuerpo = 'caprabo.cupones.preferencia.cuerpo-ec-to-cd';
        botonAceptar = 'caprabo.cupones.preferencia.boton-activarCupones';
        pie = 'caprabo.cupones.preferencia.pie-ec-cd';
      }
    } else if (isCCtoCD) {
      titulo = 'vales.preferencia.titulo-desde-cliente';
      if (isEroski) {
        cuerpo = 'vales.preferencia.cuerpo-cc-to-cd';
        botonAceptar = 'vales.preferencia.boton-activarVales';
        pie = 'vales.preferencia.pie-ec-cd';
      } else if (isCaprabo) {
        cuerpo = 'caprabo.cupones.preferencia.cuerpo-cc-to-cd';
        botonAceptar = 'caprabo.cupones.preferencia.boton-activarCupones';
        pie = 'caprabo.cupones.preferencia.pie-ec-cd';
      }
    } else if (isEDtoCC) {
      titulo = 'vales.preferencia.titulo-desde-eroski';
      botonAceptar = 'vales.preferencia.boton-confirmar';
      if (isEroski) {
        cuerpo = 'vales.preferencia.cuerpo-ed-to-cc';
      } else if (isCaprabo) {
        cuerpo = 'caprabo.cupones.preferencia.cuerpo-ed-to-cc';
      }
    } else if (isCDtoCC) {
      titulo = 'vales.preferencia.titulo-desde-cliente';
      botonAceptar = 'vales.preferencia.boton-activarVales-cd-to-cc';
      if (isEroski) {
        cuerpo = 'vales.preferencia.cuerpo-cd-to-cc';
      } else if (isCaprabo) {
        cuerpo = 'caprabo.cupones.preferencia.cuerpo-cd-to-cc';
      }
    }
    setModalTitleTxt(titulo);
    setModalBodyTxt(cuerpo);
    setModalBtnCancelTxt('vales.preferencia.boton-cancelar');
    setModalBtnAceptTxt(botonAceptar);
    setModalPieTxt(pie);
  }

  function avisoModPreferencia(keyPreferencia: string) {
    const {
      isECtoCD,
      isCCtoCD,
      isEDtoCC,
      isCDtoCC,
    } = tiposAvisoPreferenciaModal(keyPreferencia);
    const mostrarModal: boolean = isECtoCD || isCCtoCD || isEDtoCC || isCDtoCC;
    if (mostrarModal) {
      buildTextPreferencia(isECtoCD, isCCtoCD, isEDtoCC, isCDtoCC);
      setAceptaCambioPreferencia('0');
      setShowModalPreferencia(true);
    }
    return mostrarModal;
  }

  useEffect(() => {
    if (load) {
      const padre = document.getElementById(
        `datos__bloque-preferencia`,
      ) as HTMLDivElement;
      const scrollPreferencia = StorageHelper.getScrollPreferencia() as string;
      if (
        padre !== null &&
        scrollPreferencia !== null &&
        scrollPreferencia !== undefined
      ) {
        const misDatosHelper = new MisDatosHelper();
        misDatosHelper.scrollToResponsive(padre);
        StorageHelper.removeScrollPreferencia();
      }
    }
  });

  useEffect(() => {
    if (dataUser.user.token !== '') getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUser.user.token]);

  useEffect(() => {
    if (save && Boolean(parseInt(aceptaCambioPreferencia, 10))) {
      modificarPreferencia();
    }
    setAceptaCambioPreferencia('-1');
    setSave(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [save]);

  const handlerChange = (
    value: React.ChangeEvent<HTMLInputElement>,
    keyPreferenciaCliente: string,
  ) => {
    const valorCheck = value.target.checked;
    let guardar = false;
    let seMuestraAviso = false;
    if (valorCheck) {
      const mostrarAviso = avisoModPreferencia(keyPreferenciaCliente);
      seMuestraAviso = mostrarAviso;
      guardar = !mostrarAviso;
    }
    if (seMuestraAviso) {
      setPreference(keyPreferenciaCliente);
      setKeyPendingModal(keyPreferenciaCliente);
      setCheckPendingModal(valorCheck);
    } else {
      setCheckPendingModal(false);
      if (guardar) {
        setPreference(keyPreferenciaCliente);
        setSave(guardar);
      } else {
        actualizarPreferencia(keyPreferenciaCliente, valorCheck);
      }
    }
  };

  return (
    <>
      {(() => {
        if (load) {
          return (
            <>
              <div className="divider" />
              <div className="bloque-datos" id="datos__bloque-preferencia">
                <h2 className="bloque-datos__title u-text-brand">
                  <span className="divider__square brand-color" />
                  <span className="u-margin-right">{t(tituloPreferencia)}</span>
                </h2>

                <div className="bloque-datos__datos">
                  <p className="u-margin-bottom">{t(subTituloPreferencia)}</p>

                  <form className="check-options">
                    <fieldset>
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="digital" className="checkgroup">
                        {t('datos.conf-vales-ahorro.digital-option')}
                        <input
                          type="checkbox"
                          id="digital"
                          className="form-control"
                          checked={isClienteDigital}
                          onChange={(e) =>
                            handlerChange(
                              e,
                              MisDatosConstants.CLIENTE_PROMOCION_DIGITAL,
                            )
                          }
                        />
                        <span className="checkmark" />
                      </label>

                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="papel" className="checkgroup">
                        {t('datos.conf-vales-ahorro.papel-option')}
                        <input
                          type="checkbox"
                          id="papel"
                          className="form-control"
                          checked={isClienteCaja}
                          onChange={(e) =>
                            handlerChange(
                              e,
                              MisDatosConstants.CLIENTE_PROMOCION_CAJA,
                            )
                          }
                        />
                        <span className="checkmark" />
                      </label>

                      {/* <div className="radio radio-inline"> */}
                      {/*  <input */}
                      {/*    type="radio" */}
                      {/*    name="valeAhorro" */}
                      {/*    id="digital" */}
                      {/*    value="digital" */}
                      {/*    className="form-control" */}
                      {/*  /> */}
                      {/*  /!* eslint-disable-next-line jsx-a11y/label-has-associated-control *!/ */}
                      {/*  <label htmlFor="hombre" className="control-label"> */}
                      {/*    Digital */}
                      {/*  </label> */}
                      {/* </div> */}
                      {/* <div className="radio radio-inline"> */}
                      {/*  <input */}
                      {/*    type="radio" */}
                      {/*    name="valeAhorro" */}
                      {/*    id="papel" */}
                      {/*    value="papel" */}
                      {/*    className="form-control" */}
                      {/*  /> */}
                      {/*  /!* eslint-disable-next-line jsx-a11y/label-has-associated-control *!/ */}
                      {/*  <label htmlFor="mujer" className="control-label"> */}
                      {/*    En papel al pasar por caja */}
                      {/*  </label> */}
                      {/* </div> */}
                    </fieldset>
                  </form>
                </div>
              </div>
              <ModalConfirmacion
                isBig={false}
                isSmall
                isOro={false}
                textCentered
                showModal={showModalPreferencia}
                setShowModal={setShowModalPreferencia}
                title={t(modalTitleTxt)}
                textHTMLContent={t(modalBodyTxt)}
                doAceptar={pulsaBtnAceptar}
                doClose={pulsaBtnCancelar}
                showCancel
                acceptButtonTextKey={t(modalBtnAceptTxt)}
                cancelButtonTextKey={t(modalBtnCancelTxt)}
                pie={t(modalPieTxt)}
                hideXButton={false}
              />
            </>
          );
        }
        return false;
      })()}
    </>
  );
};

export default PreferenciaModVales;
